import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { matchIsValidTel,MuiTelInput } from 'mui-tel-input';

import { type EmergencyContact } from '../../types/types';

interface EmergencyContactCardProps {
  contact: EmergencyContact | null | undefined;
  onChange: (contact: EmergencyContact | null) => void;
  onBlur?: () => void;
  errors?: Record<string, string>;
  highlightedFields?: string[];
  disabled?: boolean;
}

const EmergencyContactCard: React.FC<EmergencyContactCardProps> = ({
  contact,
  onChange,
  onBlur,
  errors = {},
  highlightedFields = [],
  disabled = false,
}) => {
  const handleFieldChange = (field: keyof EmergencyContact, value: string) => {
    const updatedContact: EmergencyContact = {
      ...(contact || {}),
      [field]: value || null,
    };
    onChange(updatedContact);
  };

  const handlePhoneChange = (newValue: string) => {
    // Convert to E.164 format if valid
    if (matchIsValidTel(newValue)) {
      // Extract just the digits and country code
      const cleaned = newValue.replace(/\D/g, '');
      const e164 = cleaned.startsWith('1') ? `+${cleaned}` : `+1${cleaned}`;
      handleFieldChange('phone', e164);
    } else {
      handleFieldChange('phone', newValue);
    }
  };

  const formatPhoneDisplay = (phone: string | null | undefined): string => {
    if (!phone) return '';
    // If it's already in E.164, mui-tel-input will format it nicely
    return phone;
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
        Emergency Contact
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Name"
            value={contact?.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              ...(highlightedFields.includes('emergency_contact_name') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Relationship"
            value={contact?.relationship || ''}
            onChange={(e) => handleFieldChange('relationship', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.relationship}
            helperText={errors.relationship || 'e.g., Spouse, Parent, Sibling, Friend'}
            sx={{
              ...(highlightedFields.includes('emergency_contact_relationship') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <MuiTelInput
            label="Contact Phone"
            value={formatPhoneDisplay(contact?.phone)}
            onChange={handlePhoneChange}
            onBlur={onBlur}
            defaultCountry="US"
            forceCallingCode
            onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
            size="small"
            disabled={disabled}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{
              width: '100%',
              '& input': {
                height: '1.4375em',
              },
              ...(highlightedFields.includes('emergency_contact_phone') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Email"
            type="email"
            value={contact?.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              ...(highlightedFields.includes('emergency_contact_email') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmergencyContactCard;